import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  TicketingAnalyticsCustomDateRange,
  TicketingAnalyticsSelectedFilters,
  TicketingTimePeriodFilterPayload,
  TimePeriod,
} from '../../screens/NavigatorReports/components/types';
import {
  deriveFiscalQuarter,
  deriveWeekInYear,
} from '../../screens/NavigatorReports/util/timePeriodUtils';

interface PeriodWeekContext {
  period?: number;
  week?: number;
  year?: number;
  date?: string;
  quarter?: number;
}

export interface TicketingAnalyticsFiltersPayload {
  message: string;
  data: {
    timePeriod: TimePeriod;
    timePeriodFilter: TicketingTimePeriodFilterPayload;
    customDateRange: TicketingAnalyticsCustomDateRange;
    selectedFilters: TicketingAnalyticsSelectedFilters;
  };
  metadata: {
    clientTimestamp: string;
    clientTimezone: string;
    usedClientTime: boolean;
  };
}

export interface TicketingAnalyticsFiltersState {
  latestPayload: TicketingAnalyticsFiltersPayload;
}

const createEmptySelection = (): TicketingAnalyticsSelectedFilters => ({
  regions: [],
  markets: [],
  locations: [],
  categories: [],
  subCategories: [],
  tertiaries: [],
  statuses: [],
  resolutionPaths: [],
  urgencies: [],
  assignedTo: [],
});

const createEmptyCustomDateRange = (): TicketingAnalyticsCustomDateRange => ({
  startDate: null,
  endDate: null,
});

const createMetadata = () => ({
  clientTimestamp: new Date().toISOString(),
  clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  usedClientTime: true,
});

const formatAsYyyyDdMm = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) {
    return isoDate;
  }
  return `${year}-${day}-${month}`;
};

const getTodayIso = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const buildTicketingTimePeriodFilter = (
  timePeriod: TimePeriod,
  customDateRange: TicketingAnalyticsCustomDateRange,
  periodWeekContext?: PeriodWeekContext,
): TicketingTimePeriodFilterPayload => {
  const year = String(periodWeekContext?.year ?? new Date().getFullYear());
  const baseIsoDate = periodWeekContext?.date ?? getTodayIso();
  // Use YYYY-MM-DD directly — the fiscal-date-range backend expects standard ISO date format.
  // The old YYYY-DD-MM conversion (formatAsYyyyDdMm) was causing the backend to interpret
  // August 7 as July 8, June 8, etc. depending on the day/month values.
  const dayValue = baseIsoDate; // "2026-08-07" stays as "2026-08-07"

  if (timePeriod === 'custom') {
    // Keep YYYY-MM-DD as-is — do NOT convert to YYYY-DD-MM.
    // maybeIsoDate in the API layer parses YYYY-MM-DD directly.
    return {
      filter: 'custom',
      value: {
        start: customDateRange.startDate ?? null,
        end: customDateRange.endDate ?? null,
      },
    };
  }

  if (timePeriod === 'day') {
    return { filter: 'day', value: dayValue, year };
  }

  if (timePeriod === 'period') {
    return { filter: 'period', value: String(periodWeekContext?.period ?? ''), year };
  }

  if (timePeriod === 'quarter') {
    // `periodWeekContext` only carries { period, week, year }, so the quarter must be
    // derived from the fiscal period rather than read off a field that is never set.
    const quarter = periodWeekContext?.quarter ?? deriveFiscalQuarter(periodWeekContext?.period);
    return { filter: 'quater', value: String(quarter ?? ''), year };
  }

  if (timePeriod === 'year') {
    return { filter: 'year', value: year, year };
  }

  // Week-in-year (1–52) derived from period and week-in-period.
  const weekInYear = deriveWeekInYear(periodWeekContext?.period, periodWeekContext?.week);
  return { filter: 'week', value: String(weekInYear ?? ''), year };
};

const createPayload = (
  timePeriod: TimePeriod,
  selectedFilters: TicketingAnalyticsSelectedFilters,
  customDateRange: TicketingAnalyticsCustomDateRange,
  periodWeekContext?: PeriodWeekContext,
): TicketingAnalyticsFiltersPayload => ({
  message: 'Success',
  data: {
    timePeriod,
    timePeriodFilter: buildTicketingTimePeriodFilter(timePeriod, customDateRange, periodWeekContext),
    customDateRange,
    selectedFilters,
  },
  metadata: createMetadata(),
});

const initialState: TicketingAnalyticsFiltersState = {
  latestPayload: createPayload('week', createEmptySelection(), createEmptyCustomDateRange()),
};

const ticketingAnalyticsFiltersSlice = createSlice({
  name: 'ticketingAnalyticsFilters',
  initialState,
  reducers: {
    setTicketingAnalyticsFiltersPayload: (
      state,
      action: PayloadAction<TicketingAnalyticsFiltersPayload>,
    ) => {
      state.latestPayload = action.payload;
    },
    resetTicketingAnalyticsFiltersPayload: (state) => {
      state.latestPayload = createPayload('week', createEmptySelection(), createEmptyCustomDateRange());
    },
  },
});

export const {
  setTicketingAnalyticsFiltersPayload,
  resetTicketingAnalyticsFiltersPayload,
} = ticketingAnalyticsFiltersSlice.actions;

export const buildTicketingAnalyticsFiltersPayload = (
  timePeriod: TimePeriod,
  selectedFilters: TicketingAnalyticsSelectedFilters,
  customDateRange: TicketingAnalyticsCustomDateRange,
  periodWeekContext?: PeriodWeekContext,
): TicketingAnalyticsFiltersPayload => {
  return createPayload(timePeriod, selectedFilters, customDateRange, periodWeekContext);
};

export default ticketingAnalyticsFiltersSlice.reducer;
